/**
 * A patient's portal access state — the API's `PatientPortalAccessDto`, returned by
 * `GET /Patients/{patientId}/PortalAccess`.
 *
 * It answers two things the identity record cannot: whether staff have blocked the patient out of
 * the portal, and which portal accounts are linked to them. Everything the Access & Controls
 * detail screen says about access comes from here.
 */
export interface PatientPortalAccess {
  patientId: number;
  /**
   * True while a staff block is in force. A block is a property of the *patient*, not of any one
   * link — a patient with no linked accounts at all can still be blocked, and blocking leaves
   * every `UserPatient` row untouched (see {@link PatientPortalAccessUser.statusType}).
   */
  isBlocked: boolean;
  /** The block in force, or null when access is not blocked. */
  block: PatientPortalAccessBlock | null;
  /**
   * Every portal account linked to the patient — the patient's own (Self) account and revoked
   * links included. Soft-deleted links and accounts are already excluded by the API.
   *
   * An empty array is the real answer "nobody is linked to this patient", which is distinct from
   * the request having failed; the screen says those two differently.
   */
  linkedUsers: PatientPortalAccessUser[];
}

/** Who blocked a patient's portal access, when, and why. */
export interface PatientPortalAccessBlock {
  patientPortalAccessBlockHistoryId: number;
  /** Null when the block was recorded without one. */
  patientPortalAccessBlockReasonTypeId: number | null;
  /**
   * The label the Block Reason dropdown shows, e.g. "Inmate / Sheriff's Dept. Request" — the
   * lookup table's `Description`, not the stable enum spelling.
   */
  patientPortalAccessBlockReasonType: string | null;
  /** Staff context recorded with the block. */
  notes: string | null;
  /** UTC ISO timestamp. */
  blockedOnUtc: string;
  blockedByPatientPortalAdminUserId: number;
  /** The acting admin's "First Last", or a placeholder when the admin row can't be resolved. */
  blockedByDisplayName: string;
}

/**
 * Where one link stands. Ids are the contract with `pat.UserPatientStatusType`.
 *
 * There is no "Pending" here and no expiry: a link exists once it has been granted, `Revoked` is
 * terminal, and the portal carries no end date for a grant. Access *awaiting* a decision lives on
 * the Proxy Verifications queue, not on this list.
 */
export enum UserPatientStatusType {
  Active = 1,
  Revoked = 2,
}

/** One portal account linked to a patient (a `pat.UserPatient` row). */
export interface PatientPortalAccessUser {
  /** The link id — and the id a revoke acts on, not the portal user's id. */
  userPatientId: number;
  patientPortalUserId: number;
  /** The portal user's "First Last". */
  displayName: string;
  relationshipTypeId: number;
  /** The user's relationship to the patient, e.g. "Self", "Guardian", "Spouse". */
  relationshipType: string;
  /** False for the patient's own (Self) account. Only a proxy link can be revoked. */
  isProxy: boolean;
  /** See {@link UserPatientStatusType}. */
  userPatientStatusTypeId: number;
  /**
   * "Active" / "Revoked", as the API spells it. A staff block is *not* a link status — a blocked
   * patient's links all stay Active, and `isBlocked` on the parent is what says nobody can get in.
   */
  userPatientStatusType: string;
  /** UTC ISO timestamp the link was added. */
  createdOnUtc: string;
}

/**
 * The API's `PatientPortalAccessBlockReasonType` enum. Its ids are the contract with
 * `pat.PatientPortalAccessBlockReasonType` ("do not renumber"); the name and label are database
 * columns. The labels still come from the lookup — this is only for the one reason the screen
 * treats differently.
 */
export enum PatientPortalAccessBlockReasonTypeId {
  InmateOrSheriffRequest = 1,
  PatientRequest = 2,
  SecurityConcern = 3,
  Other = 4,
}

/**
 * One option in the Block Reason dropdown — the API's `PatientPortalAccessBlockReasonTypeDto`,
 * from `GET /PatientPortalAccessBlockReasonTypes`.
 *
 * Fetched rather than hard-coded in the UI because the block request is keyed on the **id**, and
 * the label staff read lives in `pat.PatientPortalAccessBlockReasonType.Description` — a list
 * written out here would be a second source of truth for both, and a reason renamed in the
 * database would quietly stop matching the block already on file.
 */
export interface PatientPortalAccessBlockReasonType {
  patientPortalAccessBlockReasonTypeId: number;
  /** The stable enum spelling, e.g. "InmateOrSheriffRequest". Not for display. */
  name: string;
  /** The label to render, e.g. "Inmate / Sheriff's Dept. Request". */
  description: string;
}

/**
 * Body of `PUT /Patients/{patientId}/PortalAccess/Block` (the API's
 * `PatientPortalAccessBlockRequest`).
 */
export interface PatientPortalAccessBlockRequest {
  /**
   * Required, and validated against the `PatientPortalAccessBlockReasonType` enum — an id outside
   * it is a 400, and so is the `0` an unpicked dropdown would send. The screen gates on a reason
   * being chosen before it sends.
   */
  patientPortalAccessBlockReasonTypeId: number;
  /**
   * Staff context, trimmed and max 500 characters server-side. Null when none is given.
   *
   * The card's Comment field. Optional, except that the screen requires it when the reason is
   * "Other"; the server does not enforce that rule. The API's own note: it must not carry patient
   * PII/PHI.
   */
  notes: string | null;
}

/**
 * Body of `PUT /Patients/{patientId}/PortalAccess/Restore` (the API's
 * `PatientPortalAccessRestoreRequest`).
 *
 * Note there is no reason here, unlike a block: restoring is recorded against the acting admin and
 * the history row, and the server asks only for optional context.
 */
export interface PatientPortalAccessRestoreRequest {
  /**
   * Staff context, trimmed and max 500 characters server-side. Null when none is given.
   *
   * The API's own note: it must not carry patient PII/PHI. There is no notes input on the card
   * yet, so this screen sends null.
   */
  notes: string | null;
}

/**
 * Result of a restore — the API's `PatientPortalAccessRestoreResultDto`.
 *
 * A different shape from the block response, and deliberately so: the restore both changes access
 * and tries to tell the patient, and those two succeed independently.
 */
export interface PatientPortalAccessRestoreResult {
  /** The patient's access state after the restore. */
  patientPortalAccess: PatientPortalAccess;
  /**
   * True only when a restore notification was actually handed off.
   *
   * **Branch the "patient has been notified" wording on this, never on the status code** — the
   * API says so outright. Notifying happens after the commit and is best-effort, so a `false`
   * here sits on a restore that did go through: the access is back and nobody has told the
   * patient, which is the one case where a staff member has to pick up the phone.
   */
  isPatientNotified: boolean;
}
